// FILE: /src/Components/Redirectpage/GMBRedirect.js (NEW FILE)

import React, { useEffect, useState } from "react";
import { Spin, message, Result, Button, Checkbox } from 'antd';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as authServices from '../../services/authConnect';
import { handleAuthSuccess } from "../../store/Slices/GroupSlice";

const LocationSelectionView = ({ locations, onFinalize, onCancel }) => {
    const [selectedItems, setSelectedItems] = useState([]);

    const handleItemSelect = (item, isChecked) => {
        setSelectedItems(prev => 
            isChecked ? [...prev, item] : prev.filter(i => i.name !== item.name)
        );
    };

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
            <div className="w-full max-w-lg bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-semibold mb-2">Select Google Business Locations</h3>
                <p className="text-gray-500 mb-4">Choose which business locations you'd like to connect.</p>
                <div className="h-72 overflow-y-auto border rounded-lg p-2">
                    {locations.map(loc => (
                        <div key={loc.name} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                            <div className="flex flex-col">
                                <span className="font-semibold">{loc.title}</span>
                                <span className="text-xs text-gray-500">{loc.storefrontAddress?.addressLines?.join(', ')}</span>
                            </div>
                            <Checkbox onChange={(e) => handleItemSelect(loc, e.target.checked)} checked={selectedItems.some(i => i.name === loc.name)} />
                        </div>
                    ))}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button key="back" onClick={onCancel}>Cancel</Button>
                    <Button key="submit" type="primary" onClick={() => onFinalize(selectedItems)} disabled={selectedItems.length === 0}>
                        Connect ({selectedItems.length})
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default function GMBRedirect() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [status, setStatus] = useState('authenticating');
    const [error, setError] = useState('');
    const [selectionData, setSelectionData] = useState(null);

    useEffect(() => {
        const handleCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const state = params.get('state');

            if (code && state) {
                try {
                    const response = await authServices.handleCallback('GMB', code, state);
                    if (response.requiresSelection) {
                        setSelectionData(response);
                        setStatus('selecting');
                    } else {
                        // This case is unlikely for GMB but handled for consistency
                        message.success("Google Business Profile connected!");
                        dispatch(handleAuthSuccess());
                        navigate('/Dashboard/GroupsProfile');
                    }
                } catch (err) {
                    setError(err.message || 'Failed to verify connection with Google.');
                    setStatus('error');
                }
            } else {
                setError('Google authentication was cancelled or failed.');
                setStatus('error');
            }
        };
        handleCallback();
    }, [dispatch, navigate]);

    const handleFinalize = async (selectedItems) => {
        if (selectedItems.length === 0) {
            message.info("Please select at least one location.");
            return;
        }
        setStatus('finalizing');
        try {
            await authServices.finalize('GMB', selectedItems, selectionData.stateToken);
            message.success(`${selectedItems.length} location(s) connected successfully!`);
            dispatch(handleAuthSuccess());
            navigate('/Dashboard/GroupsProfile');
        } catch (err) {
            setError(err.message || "Failed to save selected locations.");
            setStatus('error');
        }
    };

    if (status === 'authenticating' || status === 'finalizing') {
        return (
            <div className="flex flex-col justify-center items-center h-screen">
                <Spin size="large" />
                <p className="mt-4 text-lg text-gray-600">{status === 'finalizing' ? 'Saving locations...' : 'Authenticating with Google...'}</p>
            </div>
        );
    }
    
    if (status === 'error') {
        return (
             <Result
                status="error"
                title="Connection Failed"
                subTitle={error}
                extra={[
                    <Button type="primary" key="console" onClick={() => navigate('/Dashboard/GroupsProfile')}>
                        Go to Brands & Channels
                    </Button>,
                ]}
            />
        );
    }

    if (status === 'selecting') {
        return (
            <LocationSelectionView
                locations={selectionData.locations || []}
                onFinalize={handleFinalize}
                onCancel={() => navigate('/Dashboard/GroupsProfile')}
            />
        );
    }

    return null;
}